import { computed, observable } from 'mobx';
import { IServiceSelectionItemBlockStyles } from '~/types/block-styles.types';
import { CXServiceItemBlock } from '~/components/ClientExperience/OneFlow/models/cx-service-item-block.model';
import type { CXServiceSelectionBlock } from '~/components/ClientExperience/OneFlow/models/cx-service-selection-block.model';
import { IService } from '~/types/model.types';
import {
    IServiceSelectionItemBlockResponse,
    ISessionConfig
} from '~/types/server.types';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import type { FlowRevision } from '~/models/flow-revision.model';

export class CXServiceSelectionItemBlock extends CXServiceItemBlock {
    type: 'service_selection_item_block';
    @observable styles: IServiceSelectionItemBlockStyles;
    @observable invoice_service_item: IService;
    @observable allow_custom_quantity: boolean;
    @observable min_quantity: number;
    @observable max_quantity: number;
    @observable session_config_id: string | null;
    @observable session_config: ISessionConfig | null;
    @observable session_config_hash: string | null;

    constructor(
        block: IServiceSelectionItemBlockResponse,
        flowRevision: FlowRevision<CXRootStore>,
        rootStore: CXRootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
        this.connected_variable_id = block.connected_variable_id;
        this.allow_custom_quantity = block.allow_custom_quantity;
        this.min_quantity = block.min_quantity;
        this.max_quantity = block.max_quantity;
        this.session_config_id = block.session_config_id;
        this.session_config = block.session_config;
    }

    @computed
    get isCustomQuantity() {
        return (
            this.allow_custom_quantity &&
            this.serviceSelectionBlock.hasSelection
        );
    }

    @computed
    get subItems() {
        return this.childBlocks as CXServiceSelectionItemBlock[];
    }

    @computed
    get isSomeSubItemsCustomQuantity() {
        return (
            this.isPackage &&
            this.subItems.some(subItem => subItem.isCustomQuantity)
        );
    }

    @computed
    get serviceSelectionBlock(): CXServiceSelectionBlock {
        let { parent } = this;

        while (parent && parent.type !== 'service_selection_block') {
            parent = parent.parent;
        }

        return parent as CXServiceSelectionBlock;
    }

    @computed
    get selectedQuantity(): number {
        if (!this.isCustomQuantity) {
            return this.service_item.quantity;
        }

        return this.connectedVariable?.value?.quantity || this.min_quantity;
    }

    @computed
    get totalPrice() {
        return (
            (this.selectedQuantity ?? 1) * this.service_item.price_per_unit || 0
        );
    }

    @computed
    get subItemsTotalPrice(): number {
        return this.childBlocks.reduce((total, subItem) => {
            return total + (subItem as CXServiceSelectionItemBlock).totalPrice;
        }, 0);
    }

    @computed
    get isSubItem() {
        return this.parent instanceof CXServiceSelectionItemBlock;
    }

    @computed
    get isServiceSelected() {
        const potentialSelectedBlock = this.isSubItem
            ? (this.parent as CXServiceSelectionItemBlock)
            : this;
        return this.serviceSelectionBlock?.selectedServiceBlocks.includes(
            potentialSelectedBlock
        );
    }

    getDataForInvoice() {
        return this.invoice_service_item || this.service_item;
    }

    get isServiceSelectionItemBlock() {
        return true;
    }

    @computed
    get totalPriceIncludingSubItems() {
        return this.totalPrice + this.subItemsTotalPrice;
    }
}
